import React from 'react';

const JobSearchInstructions = ({ show, onClose }) => {
  if (!show) return null;

  return (
    <div className="card mb-4 border-secondary">
      <div className="card-header d-flex justify-content-between align-items-center bg-light">
        <h5 className="mb-0">
          <i className="fas fa-question-circle me-2 text-secondary"></i>
          Instructions for Job Search
        </h5>
        <button
          type="button"
          className="btn-close"
          onClick={onClose}
          aria-label="Close"
        ></button>
      </div>
      <div className="card-body">
        <p className="text-muted mb-3">
          This page pulls live job listings from a proprietary, third-party database of real employer job postings sourced directly from 175,000+ company career sites
          and 42+ ATS platforms (Workday, Greenhouse, Lever, Ashby, Rippling, iCIMS, etc.),
          enriched with AI analysis and LinkedIn company data.
        </p>

        <div className="row g-4">
          {/* Step 1 */}
          <div className="col-md-6">
            <div className="d-flex gap-3">
              <div className="flex-shrink-0">
                <span
                  className="badge bg-primary rounded-circle"
                  style={{ width: '28px', height: '28px', lineHeight: '20px', fontSize: '14px' }}
                >
                  1
                </span>
              </div>
              <div>
                <h6 className="mb-1">Select a Time Window</h6>
                <p className="text-muted small mb-0">
                  Use the dropdown at the top right to choose how far back to look &mdash; e.g.{' '}
                  <strong>Last 24 Hours</strong>, <strong>Last 3 Days</strong>, or{' '}
                  <strong>Last 7 Days</strong>. Narrower windows return fresher results. Premium
                  plan endpoints unlock longer ranges.
                </p>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="col-md-6">
            <div className="d-flex gap-3">
              <div className="flex-shrink-0">
                <span
                  className="badge bg-primary rounded-circle"
                  style={{ width: '28px', height: '28px', lineHeight: '20px', fontSize: '14px' }}
                >
                  2
                </span>
              </div>
              <div>
                <h6 className="mb-1">Apply Filters &amp; Click &ldquo;Apply&rdquo;</h6>
                <p className="text-muted small mb-0">
                  Filters are <strong>not applied automatically</strong> &mdash; set your desired
                  filters and click <strong>Apply</strong> (or the refresh button) to fetch results.
                  Clicking &ldquo;Clear&rdquo; resets filter values but does not re-fetch.
                </p>
              </div>
            </div>
          </div>

          {/* Basic Filters */}
          <div className="col-12">
            <h6 className="text-primary border-bottom pb-1 mb-2">
              <i className="fas fa-search me-2"></i>Basic Filters
            </h6>
            <div className="row g-2 small">
              <div className="col-md-6">
                <strong>Search</strong> &mdash; keyword match across job title, company name, and
                description. Supports prefix matching: e.g. <code>Soft:_</code> matches
                &ldquo;Software&rdquo;, &ldquo;Softball&rdquo;, etc.
              </div>
              <div className="col-md-6">
                <strong>Location</strong> &mdash; spell out full names; avoid abbreviations. Write{' '}
                <code>New York</code> instead of <code>NY</code>, <code>United States</code>{' '}
                instead of <code>US</code>.
              </div>
              <div className="col-md-6">
                <strong>Advanced Company Filter</strong> &mdash; powerful filter for company names
                with advanced operators:
                <ul className="small mb-0 mt-1">
                  <li>
                    <code>|</code> (OR): <code>Google | Microsoft | Amazon</code> matches any of
                    these companies
                  </li>
                  <li>
                    <code>&amp;</code> (AND): <code>Tech &amp; Innovate</code> matches companies
                    with both words
                  </li>
                  <li>
                    <code>!</code> (NOT): <code>University &amp; ! Harvard</code> matches all
                    universities except Harvard
                  </li>
                  <li>
                    <code>:*</code> (Prefix): <code>Tech:*</code> matches TechCorp, Technology Inc,
                    etc.
                  </li>
                  <li>
                    <code>&apos; &apos;</code> (Phrase): <code>&apos;JP Morgan&apos;</code> matches
                    exact phrase (use for multi-word names)
                  </li>
                  <li>
                    You can combine these filters in a powerful way. For example, <code>!&apos;Health System&apos;:* </code> searches for companies that <b>do not contain the phrase &quot;Health System&quot; in the company name</b>
                  </li>
                </ul>
              </div>
              <div className="col-md-6">
                <strong>Results Limit</strong> &mdash; maximum number of jobs to return per search
                (10 &ndash; 500). Higher limits take longer to load. The count shown in the toolbar
                reflects how many were actually returned.
              </div>
              <div className="col-md-6">
                <strong>Offset (Skip)</strong> &mdash; skip the first <em>N</em> results before
                returning. Use together with Limit to page through large result sets &mdash; e.g.
                Limit&nbsp;100 + Offset&nbsp;100 gives you jobs 101&ndash;200. The toolbar will
                show the range (e.g. <em>Showing 101&ndash;200</em>) after each search.
              </div>
              <div className="col-md-6">
                <strong>Employment Type</strong> &mdash; filters by the raw employment type field
                reported by the employer&apos;s ATS (e.g. Full-time, Part-time, Contract).
              </div>
            </div>
          </div>

          {/* AI Filters */}
          <div className="col-12">
            <h6 className="text-primary border-bottom pb-1 mb-2">
              <i className="fas fa-robot me-2"></i>AI-Enriched Filters{' '}
              <span className="badge bg-warning text-dark ms-1" style={{ fontSize: '0.7em' }}>
                BETA
              </span>
            </h6>
            <p className="text-muted small mb-2">
              These fields are derived by AI analysis of the raw job description and may not be
              100% accurate. Use them to narrow results but verify important details in the posting
              itself.
            </p>
            <div className="row g-2 small">
              <div className="col-md-6">
                <strong>Remote</strong> &mdash; filters by the job&apos;s remote flag. Note: this
                field is sensitive and will also match jobs that mention &ldquo;remote&rdquo;
                anywhere in the title, location, or description.
              </div>
              <div className="col-md-6">
                <strong>Work Arrangement</strong> &mdash; AI-classified as <em>On-site</em>,{' '}
                <em>Hybrid</em>, <em>Remote OK</em>, or <em>Remote Solely</em>.
              </div>
              <div className="col-md-6">
                <strong>Experience Level</strong> &mdash; AI-estimated years of experience required:
                0&ndash;2, 2&ndash;5, 5&ndash;10, or 10+.
              </div>
              <div className="col-md-6">
                <strong>Has Salary</strong> &mdash; whether the posting includes any salary
                information.
              </div>
              <div className="col-md-6">
                <strong>Visa Sponsorship</strong> &mdash; whether the posting mentions visa/work
                authorization sponsorship.
              </div>
              <div className="col-md-6">
                <strong>AI Employment Type</strong> &mdash; AI-standardized employment type
                (FULL_TIME, PART_TIME, CONTRACTOR, etc.).
              </div>
              <div className="col-md-6">
                <strong>Job Category (Taxonomy)</strong> &mdash; AI-assigned job function category.
                Counts shown next to each option reflect the current loaded dataset.
              </div>
            </div>
          </div>

          {/* LinkedIn Filters */}
          <div className="col-12">
            <h6 className="text-primary border-bottom pb-1 mb-2">
              <i className="fab fa-linkedin me-2"></i>LinkedIn Company Filters{' '}
              <span className="badge bg-warning text-dark ms-1" style={{ fontSize: '0.7em' }}>
                BETA
              </span>
            </h6>
            <div className="row g-2 small">
              <div className="col-md-6">
                <strong>Industry</strong> &mdash; LinkedIn-reported company industry. Counts reflect
                the current loaded dataset.
              </div>
              <div className="col-md-6">
                <strong>Company Slug</strong> &mdash; filter by a specific company&apos;s LinkedIn
                slug (e.g. <code>netflix</code>, <code>walmart</code>). Requires an exact match.
              </div>
              <div className="col-md-6">
                <strong>Agency Filter</strong> &mdash; choose <em>Direct Employers</em> to exclude
                staffing/recruitment agencies, or <em>Agencies Only</em> to see only agency
                postings.
              </div>
              <div className="col-md-6">
                <strong>Date Posted (After)</strong> &mdash; further restrict results to jobs posted
                on or after a specific date.
              </div>
            </div>
          </div>

          {/* Source Filters */}
          <div className="col-12">
            <h6 className="text-primary border-bottom pb-1 mb-2">
              <i className="fas fa-server me-2"></i>Source Filters
            </h6>
            <div className="row g-2 small">
              <div className="col-md-6">
                <strong>Source (ATS)</strong> &mdash; filter by the Applicant Tracking System the
                job was posted through (e.g. Workday, Greenhouse, Lever, Ashby).
              </div>
              <div className="col-md-6">
                <strong>Exclude Source</strong> &mdash; exclude a specific ATS from results. Useful
                for removing a high-volume source that&apos;s skewing your results.
              </div>
            </div>
          </div>

          {/* Export & Import */}
          <div className="col-12">
            <h6 className="text-primary border-bottom pb-1 mb-2">
              <i className="fas fa-file-export me-2"></i>Exporting &amp; Importing Jobs
            </h6>
            <div className="row g-2 small">
              <div className="col-md-6">
                <strong>Save as CSV / Save as Excel</strong> &mdash; select rows using the
                checkboxes, then click either export button to download a file containing those
                jobs. Buttons are only active when at least one row is selected. The CSV uses
                internal field names as headers so it can be re-imported without data loss.
              </div>
              <div className="col-md-6">
                <strong>Import Saved Jobs</strong> &mdash; load a previously exported CSV back into
                the grid without making a new API call. Useful for sharing job sets with teammates
                or working offline. The imported rows replace the current grid contents and support
                all grid filters, sorting, and export features. Any CSV with matching column names
                (e.g. <code>job_title</code>, <code>company</code>, <code>id</code>) will work.
              </div>
            </div>
          </div>

          {/* Adding to Leads */}
          <div className="col-12">
            <h6 className="text-primary border-bottom pb-1 mb-2">
              <i className="fas fa-user-plus me-2"></i>Adding Jobs to Sales Leads
            </h6>
            <p className="text-muted small mb-0">
              Check the boxes on the left of any rows you want to save, then click{' '}
              <strong>Add to Sales Leads</strong>. Each job is saved with its full metadata (AI
              fields, LinkedIn data, salary, etc.) as a new Sales Lead. Jobs already imported
              (matched by ID) will return a duplicate error and won&apos;t be double-created.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobSearchInstructions;
